import { useEffect, useState } from "react";
import API, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { toast } from "sonner";
import { LOG_TYPES, CONDITIONS, todayISO } from "./assetUtils";

const NONE = "__none__";

export function AssetLogDialog({ open, asset, initialType = "service", onClose, onSaved }) {
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  useEffect(() => {
    if (open) setF({ type: initialType, date: todayISO(), summary: "", details: "", performed_by: "", cost: "", parts: "", condition_after: "", meter_reading: "", allow_lower: false });
  }, [open, initialType]);

  const isReading = f.type === "meter_reading";
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...f, parts: f.parts ? f.parts.split(",").map((s) => s.trim()).filter(Boolean) : [], summary: f.summary || (isReading ? `Meter reading ${f.meter_reading} ${asset.meter_unit || ""}`.trim() : "") };
      await API.post(`/assets/${asset.id}/logs`, payload);
      toast.success(isReading ? "Reading recorded" : "Logged against the asset");
      onSaved?.();
      onClose();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail) || "Couldn't save"); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="asset-log-dialog">
        <DialogHeader>
          <DialogTitle className="font-[Chivo]">Log activity — {asset?.name}</DialogTitle>
          <DialogDescription>Services and inspections reset the service clock; condition checks update the register; readings feed usage-based servicing.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs">Type</Label>
            <Select value={f.type || "service"} onValueChange={(v) => set("type", v)}><SelectTrigger data-testid="asset-log-type"><SelectValue /></SelectTrigger>
              <SelectContent>{LOG_TYPES.filter((t) => t.value !== "meter_reading" || asset?.meter_unit).map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent></Select></div>
          <div><Label className="text-xs">Date</Label><Input type="date" value={f.date || ""} onChange={(e) => set("date", e.target.value)} data-testid="asset-log-date" /></div>
          {!isReading && <div className="col-span-2"><Label className="text-xs">Summary *</Label><Input value={f.summary || ""} onChange={(e) => set("summary", e.target.value)} placeholder="e.g. Annual service — belts tensioned, filters replaced" data-testid="asset-log-summary" /></div>}
          {asset?.meter_unit && (
            <div className={isReading ? "col-span-2" : ""}><Label className="text-xs">Meter reading ({asset.meter_unit}){isReading ? " *" : ""}</Label>
              <Input type="number" step="any" value={f.meter_reading ?? ""} onChange={(e) => set("meter_reading", e.target.value)} placeholder={asset.meter_reading != null ? `Last: ${asset.meter_reading}` : ""} data-testid="asset-log-reading" />
              <label className="flex items-center gap-2 text-[11px] text-muted-foreground mt-1.5"><input type="checkbox" checked={!!f.allow_lower} onChange={(e) => set("allow_lower", e.target.checked)} data-testid="asset-log-allow-lower" /> Meter was reset or replaced (allow a lower reading)</label>
            </div>
          )}
          {!isReading && <>
            <div><Label className="text-xs">Performed by</Label><Input value={f.performed_by || ""} onChange={(e) => set("performed_by", e.target.value)} placeholder="Defaults to you" /></div>
            <div><Label className="text-xs">Cost (£)</Label><Input type="number" min="0" step="0.01" value={f.cost ?? ""} onChange={(e) => set("cost", e.target.value)} data-testid="asset-log-cost" /></div>
            <div><Label className="text-xs">Condition after</Label>
              <Select value={f.condition_after || NONE} onValueChange={(v) => set("condition_after", v === NONE ? "" : v)}><SelectTrigger data-testid="asset-log-condition"><SelectValue placeholder="Unchanged" /></SelectTrigger>
                <SelectContent><SelectItem value={NONE}>Unchanged</SelectItem>{CONDITIONS.map((c) => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}</SelectContent></Select></div>
            <div><Label className="text-xs">Parts fitted</Label><Input value={f.parts || ""} onChange={(e) => set("parts", e.target.value)} placeholder="Comma separated" data-testid="asset-log-parts" /></div>
            <div className="col-span-2"><Label className="text-xs">Details</Label><textarea value={f.details || ""} onChange={(e) => set("details", e.target.value)} rows={3} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" placeholder="Findings, readings, recommendations…" /></div>
          </>}
          <DialogFooter className="col-span-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={busy} data-testid="asset-log-submit">{busy ? "Saving…" : "Save entry"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
